import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { DefaultUrlSerializer, UrlSegmentGroup, UrlSerializer, UrlTree } from '@angular/router';

import { HttpErrorInterceptor } from './services/http-error.interceptor';

// services
import { AccessHomePageGuard } from './services/access-home-page.guard';
import { AccountDetailsService } from './services/account-details.service';
import { AdminGuardServiceGuard } from './components/admin/admin-guard-service.guard';
import { AuthGuard } from './services/auth.guard';
import { AuthService } from './services/auth.service';
import { EmailMessageService } from './services/email-message.service';
import { EmploymentExperienceService } from './services/employment-experience.service';
import { GtmService } from './services/gtm.service';
import { NavbarStateService } from './services/navbar-state.service';
import { InviteService } from './services/invite.service';
import { LearningService } from './services/learning.service';
import { MockAuthService } from './services/mock.auth.service';
import { OnboardingService } from './services/onboarding.service';
import { PlaylistService } from './services/playlist.service';
import { ProvidersService } from './services/providers.service';
import { RecommendationService } from './services/recommendation.service';
import { RegisterService } from './services/register.service';
import { RoutingStateService } from './services/routing-state.service';
import { SignatureService } from './services/signature.service';
import { SkillDuplicatesService } from './services/skill-duplicates.service';
import { SkillService } from './services/skill.service';
import { SkillsReportService } from './services/skillsreport.service';
import { SuggestionsService } from './services/suggestions.service';
import { TopSkillService } from './services/top-skill.service';
import { TrackService } from './services/track.service';
import { TrackServiceData } from './services/track.service.data';
import { UserActivitiesService } from './services/user-activities.service';
import { UserActivitiesServiceData } from './services/user-activities.service.data';
import { PublicLandingGuard } from './services/public-landing.guard';

// components
// components
import { AppComponent } from './app.component';
import { AppDetailsComponent } from './components/app-details/app-details.component';
import { AuthCallbackComponent } from './components/account/auth-callback/auth-callback.component';
import { HelpInfoComponent } from './components/help-info/help-info.component';
import { KeywordInfoComponent } from './components/keyword-info/keyword-info.component';
import { LandingV2Component } from './components/landing-v2/landing-v2.component';
import { NavbarSideComponent } from './components/navbar-side/navbar-side.component';
import { NotificationBadgeComponent } from './components/navbar-side/notification-badge/notification-badge.component';
import { PublicProfileComponent } from './components/public-profile/public-profile.component';
import { RegisterComponent } from './components/register/register.component';
import { SuggestionsComponent } from './components/suggestions/suggestions.component';
import { TermsComponent } from './components/terms/terms.component';
import { UpdateDataComponent } from './components/update-data/update-data.component';
// directives
import { ClickOutsideDirective } from './directives/click-outside.directive';
// modules
import { FeatureFlagModule } from './core/feature-flags/feature-flag.module';
import { GenericComponentsModule } from './generic-components/generic-components.module';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule, registerLocaleData } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgxGpAutocompleteModule } from "@angular-magic/ngx-gp-autocomplete";
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { NgxPicaModule } from '@digitalascetic/ngx-pica';
import { LocalStorageDirective } from 'ngx-localstorage';
import { NZ_I18N, en_US } from 'ng-zorro-antd/i18n';
import { NzAffixModule } from 'ng-zorro-antd/affix';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzConfig, NZ_CONFIG } from 'ng-zorro-antd/core/config';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzMessageModule } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationModule } from 'ng-zorro-antd/notification';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzResultModule } from 'ng-zorro-antd/result';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzSliderModule } from 'ng-zorro-antd/slider';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { ReactiveFormsModule } from '@angular/forms';
import { RollbarService, rollbarFactory, RollbarErrorHandler } from './rollbar';
import { RouterModule, Routes } from '@angular/router';

import en from '@angular/common/locales/en';
import { CareerDetailsService } from './services/career-details.service';
import { Loader } from '@googlemaps/js-api-loader';
import { PricingComponent } from './components/pricing/pricing.component';
import { MetaTagService } from './services/meta-tag.service';
registerLocaleData(en);

const ngZorroConfig: NzConfig = {
  modal: { nzMaskClosable: false },
};

// See: https://stackoverflow.com/questions/51985973/in-angular-6-how-make-case-insensitive-url-pattern
export class LowerCaseUrlSerializer extends DefaultUrlSerializer {
  parse(url: string): UrlTree {
    const urlTree = super.parse(url);

    if (url.includes('/from/') && url.includes('/UPL:', 12) || url.includes('/PL:', 12) || url.includes('activity')) {
      return urlTree;
    }

    this.lowerCaseSegments(urlTree.root);

    return urlTree;
  }

  private lowerCaseSegments(urlSegmentGroup: UrlSegmentGroup) {
    if (urlSegmentGroup.hasChildren()) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      Object.entries(urlSegmentGroup.children).forEach(([key, value]) => this.lowerCaseSegments(value));
    }

    urlSegmentGroup.segments.forEach((segment) => (segment.path = segment.path.toLowerCase()));
  }
}

const routes: Routes = [
  // Root and landing routes must be first to prevent lazy-loaded modules from intercepting
  { path: '', component: LandingV2Component, canActivate: [PublicLandingGuard], pathMatch: 'full' },
  { path: 'landing', component: LandingV2Component, canActivate: [PublicLandingGuard] },
  { path: 'auth-callback', component: AuthCallbackComponent },
  { path: 'silent-refresh.html', redirectTo: 'assets/silent-refresh.html' },
  {
    path: 'profile',
    loadChildren: () => import('./modules-profile/profile.module').then(m => m.ProfileModule),
  },
  { path: 'profile/:user', component: PublicProfileComponent },
  { path: 'profile/:user/:guildId/:permission/guild-invite', component: PublicProfileComponent },
  { path: 'register', component: RegisterComponent },
  { path: 'pricing', component: PricingComponent },
  { path: 'terms', component: TermsComponent },
  {
    path: 'recommended',
    loadChildren: () => import('./modules-recommendations/recommendation-page.module').then(m => m.RecommendationPageModule),
  },
  {
    path: 'goal',
    loadChildren: () => import('./modules-goal/goal.module').then(m => m.GoalModule),
  },
  {
    path: 'recommendations',
    loadChildren: () => import('./modules-recommendations/recommendations.module').then(m => m.RecommendationsModule),
  },
  {
    path: 'admin',
    loadChildren: () => import('./modules-admin/admin.module').then(m => m.AdminModule),
  },
  {
    path: 'account',
    loadChildren: () => import('./modules-account/account.module').then(m => m.AccountModule),
  },
  {
    path: 'guilds',
    loadChildren: () => import('./modules-guilds/guilds.module').then(m => m.GuildsModule),
  },
  { path: '**', component: LandingV2Component, canActivate: [PublicLandingGuard] }
];
@NgModule({
  declarations: [
    AppComponent,
    AppDetailsComponent,
    AuthCallbackComponent,
    ClickOutsideDirective,
    HelpInfoComponent,
    KeywordInfoComponent,
    LandingV2Component,
    NavbarSideComponent,
    NotificationBadgeComponent,
    PublicProfileComponent,
    RegisterComponent,
    SuggestionsComponent,
    TermsComponent,
    UpdateDataComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    DragDropModule,
    FeatureFlagModule,
    FormsModule,
    GenericComponentsModule,
    // GoalModule removed - importing it directly merges its routes, causing root route conflicts
    // GoalSignatureSelectorComponent is now in GenericComponentsModule, so AdminModule no longer needs GoalModule
    NgxChartsModule,
    FroalaEditorModule.forRoot(),
    FroalaViewModule.forRoot(),
    GoogleTagManagerModule.forRoot({
      id: 'GTM-P58XPRB',
      gtm_auth: 'fDkQmx0oWCYZMdCN5tfWKg',
      gtm_preview: 'env-2',
    }),
    HttpClientModule,
    InfiniteScrollDirective,
    LocalStorageDirective,
    NgxGpAutocompleteModule,
    NgxPicaModule,
    NzAffixModule,
    NzAlertModule,
    NzAutocompleteModule,
    NzBadgeModule,
    NzButtonModule,
    NzCardModule,
    NzCheckboxModule,
    NzCollapseModule,
    NzDatePickerModule,
    NzDividerModule,
    NzDrawerModule,
    NzDropDownModule,
    NzFormModule,
    NzGridModule,
    NzIconModule,
    NzImageModule,
    NzInputModule,
    NzInputNumberModule,
    NzLayoutModule,
    NzMenuModule,
    NzMessageModule,
    NzNotificationModule,
    NzModalModule,
    NzPaginationModule,
    NzPopconfirmModule,
    NzPopoverModule,
    NzProgressModule,
    NzRadioModule,
    NzResultModule,
    NzSelectModule,
    NzSkeletonModule,
    NzSliderModule,
    NzSpaceModule,
    NzSpinModule,
    NzStatisticModule,
    NzStepsModule,
    NzSwitchModule,
    NzTableModule,
    NzTabsModule,
    NzTagModule,
    NzTimelineModule,
    NzToolTipModule,
    NzTypographyModule,
    NzUploadModule,
    ReactiveFormsModule,
    RouterModule.forRoot(routes),
  ],
  exports: [RouterModule],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: UrlSerializer,
      useClass: LowerCaseUrlSerializer,
    },
    {
      provide: Loader,
      useValue: new Loader({
        apiKey: 'AIzaSyALCilOMVdUmxwqAaR1js-PN9qmLrbJG5Q',
        libraries: ['places']
      })
    },
    // AccessGoalPageGuard removed - only provided in GoalModule to prevent construction on app startup
    AccessHomePageGuard,
    AccountDetailsService,
    AdminGuardServiceGuard,
    AuthGuard,
    AuthService,
    PublicLandingGuard,
    CareerDetailsService,
    EmailMessageService,
    EmploymentExperienceService,
    GtmService,
    NavbarStateService,
    // HasGoalGuard removed - only provided in modules that use it (ProfileModule, RecommendationsModule, etc.) to prevent construction on app startup
    InviteService,
    LearningService,
    MetaTagService,
    MockAuthService,
    OnboardingService,
    PlaylistService,
    ProvidersService,
    RecommendationService,
    RegisterService,
    RoutingStateService,
    SignatureService,
    SkillService,
    SkillsReportService,
    SkillDuplicatesService,
    SuggestionsService,
    TopSkillService,
    TrackService,
    TrackServiceData,
    UserActivitiesService,
    UserActivitiesServiceData,
    { provide: NZ_I18N, useValue: en_US },
    { provide: NZ_CONFIG, useValue: ngZorroConfig },
    { provide: ErrorHandler, useClass: RollbarErrorHandler },
    // { provide: 'googleTagManagerCSPNonce', useValue: 'CSP-NONCE' },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    { provide: RollbarService, useFactory: rollbarFactory },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor() {
    // console.log('*** APP STARTING');
  }
}
